import { useSelectForms } from "@/features/forms/api/selectForms";
import { useScheduleByIdQuery } from "@/features/schedules/api/selectSchedulesById";
import type { TemplateT } from "@/types/database/quickstarts";
import { Calendar, Clipboard, Hospital } from "lucide-react";

function TrackersInfo({ template }: { template: TemplateT }) {
  const settings = template.settings;
  const { data: forms } = useSelectForms();
  const scheduleId = settings?.trackers?.[0]?.schedule_id;
  const { data: schedule } = useScheduleByIdQuery(scheduleId);

  // Helper: Get grouped tracker info
  function getTrackerGroups() {
    return (settings.trackers || []).map(
      (tracker: { form_id: string; schedule_id: string }, idx: number) => {
        const form = forms?.find((f) => f.form_id === tracker.form_id);
        const scheduleForTracker = scheduleId === tracker.schedule_id
          ? schedule
          : undefined;
        return { form, schedule: scheduleForTracker, tracker };
      },
    );
  }

  return (
    <div className="flex-1 space-y-4 p-4 min-h-0 overflow-y-auto">
      {/* Organisation at the top, most prominent */}
      {settings.organisation_name && (
        <div className="flex items-center gap-2 mb-4">
          <Hospital className="w-6 h-6 text-indigo-600" />
          <span className="font-bold text-indigo-900 dark:text-indigo-200 text-lg">
            {settings.organisation_name}
          </span>
        </div>
      )}
      {/* Forms and Schedules grouped, with visual hierarchy */}
      <div className="space-y-2">
        {getTrackerGroups().map(({ form, schedule, tracker }, idx) => (
          <div
            key={tracker?.form_id || idx}
            className="mb-2 pb-2 last:pb-0 border-b last:border-b-0"
          >
            {form && (
              <div className="flex items-center gap-2 mb-1">
                <Clipboard size={18} className="text-slate-500" />
                <span className="font-semibold text-slate-800 dark:text-slate-100 text-base">
                  {form.form_name}
                </span>
              </div>
            )}
            {schedule && (
              <div className="flex items-center gap-2 mb-1 ml-6">
                <Calendar size={16} className="text-slate-400" />
                <span className="font-medium text-slate-700 dark:text-slate-300 text-sm">
                  {schedule.schedule_name}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default TrackersInfo;
